import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { 
  Leaf, LogOut, LayoutDashboard, Users, Building2, CalendarDays,
  Search, Filter, Flame, Thermometer, Snowflake, Eye, Globe, Bell,
  TrendingUp, UserCheck, Clock, Euro, Plus, Trash2, RefreshCw, ExternalLink,
  Zap, CheckCircle, XCircle, AlertTriangle, Settings, MapPin, Download, BarChart3, Plug, Play
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import axios from "axios";
import AdminProgramsPage from "./AdminProgramsPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Navigation
const Sidebar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Déconnexion réussie");
  };

  const navItems = [
    { path: "/admin", label: "Tableau de bord", icon: LayoutDashboard },
    { path: "/admin/leads", label: "Tous les Leads", icon: Users },
    { path: "/admin/geographic", label: "Stats Géographiques", icon: MapPin },
    { path: "/admin/partners", label: "Partenaires", icon: Building2 },
    { path: "/admin/appointments", label: "Rendez-vous", icon: CalendarDays },
    { path: "/admin/programs", label: "Gestion des Primes", icon: Euro, badge: "NEW" },
    { path: "/admin/market-prices", label: "Prix du Marché", icon: Euro, badge: "NEW" },
    { path: "/admin/crm", label: "Intégration CRM", icon: Plug },
    { path: "/admin/subsidy-sources", label: "Veille Aides", icon: Globe },
    { path: "/admin/score-adjustments", label: "Scoring Dynamique", icon: Zap },
    { path: "/admin/notifications", label: "Notifications", icon: Bell },
    { path: "/admin/settings", label: "Paramètres", icon: Settings },
  ];

  return (
    <div className="w-64 bg-white border-r border-slate-100 min-h-screen flex flex-col">
      <div className="p-6 border-b border-slate-100">
        <Link to="/" className="flex items-center gap-2">
          <Leaf className="h-7 w-7 text-[#1A4D2E]" />
          <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            MesSubventions
          </span>
        </Link>
      </div>
      
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#1A4D2E] text-white' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`nav-${item.path.replace('/admin/', '') || 'dashboard'}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-orange-500 text-white rounded-full font-bold">
                      {item.badge}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 px-4 py-3 mb-3">
          <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center">
            <span className="text-[#1A4D2E] font-semibold">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </span>
          </div>
          <div>
            <p className="font-medium text-sm text-[#1D1D1D]">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-500">Administrateur</p>
          </div>
        </div>
        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 mr-3" />
          Déconnexion
        </Button>
      </div>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des statistiques");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-overview">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tableau de bord
        </h1>
        <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-total-leads">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-[#E8F5E9] rounded-lg">
              <Users className="h-5 w-5 text-[#1A4D2E]" />
            </div>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-[#1D1D1D]">{stats?.leads?.total || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Leads totaux</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-hot-leads">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <Flame className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-red-500">{stats?.leads?.by_score?.chaud || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Leads chauds</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-rdv">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <CalendarDays className="h-5 w-5 text-purple-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-purple-500">{stats?.leads?.rdv_pris || 0}</p>
          <p className="text-xs text-slate-500 mt-1">RDV pris</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-converted">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <UserCheck className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats?.leads?.converted || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Convertis</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-partners">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-[#FBE9E7] rounded-lg">
              <Building2 className="h-5 w-5 text-[#E07A5F]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#E07A5F]">{stats?.partners?.total || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Partenaires</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-sources">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Globe className="h-5 w-5 text-blue-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-blue-500">{stats?.subsidy_monitoring?.active_sources || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Sources actives</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-notifications">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Bell className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats?.subsidy_monitoring?.pending_notifications || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Notifs en attente</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="stat-total-aid">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-[#E8F5E9] rounded-lg">
              <Euro className="h-5 w-5 text-[#1A4D2E]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#1A4D2E]">{(stats?.total_estimated_aid || 0).toLocaleString('fr-FR')} €</p>
          <p className="text-xs text-slate-500 mt-1">Aides estimées</p>
        </div>
      </div>

      {/* Score Distribution */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-[#1D1D1D] mb-4">Éligibilité aux aides</h3>
          <div className="flex gap-4">
            <div className="flex-1 text-center p-4 bg-red-50 rounded-xl">
              <Flame className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-500">{stats?.leads?.by_aid_score?.chaud || stats?.leads?.by_score?.chaud || 0}</p>
              <p className="text-sm text-slate-500">Chauds</p>
            </div>
            <div className="flex-1 text-center p-4 bg-yellow-50 rounded-xl">
              <Thermometer className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-500">{stats?.leads?.by_aid_score?.tiede || stats?.leads?.by_score?.tiede || 0}</p>
              <p className="text-sm text-slate-500">Tièdes</p>
            </div>
            <div className="flex-1 text-center p-4 bg-blue-50 rounded-xl">
              <Snowflake className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-500">{stats?.leads?.by_aid_score?.froid || stats?.leads?.by_score?.froid || 0}</p>
              <p className="text-sm text-slate-500">Froids</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-[#1D1D1D] mb-4">Priorité commerciale</h3>
          <div className="flex gap-4">
            <div className="flex-1 text-center p-4 bg-red-50 rounded-xl">
              <TrendingUp className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-500">{stats?.leads?.by_business_priority?.haute || 0}</p>
              <p className="text-sm text-slate-500">Haute</p>
            </div>
            <div className="flex-1 text-center p-4 bg-yellow-50 rounded-xl">
              <TrendingUp className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-500">{stats?.leads?.by_business_priority?.moyenne || 0}</p>
              <p className="text-sm text-slate-500">Moyenne</p>
            </div>
            <div className="flex-1 text-center p-4 bg-blue-50 rounded-xl">
              <TrendingUp className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-500">{stats?.leads?.by_business_priority?.basse || 0}</p>
              <p className="text-sm text-slate-500">Basse</p>
            </div>
          </div>
        </div>
      </div>

      {/* Beneficiary Types Distribution */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <h3 className="font-semibold text-[#1D1D1D] mb-4">Répartition par type de bénéficiaire</h3>
        <div className="flex gap-4">
          <div className="flex-1 text-center p-4 bg-green-50 rounded-xl">
            <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{stats?.leads?.by_beneficiary_type?.particulier || 0}</p>
            <p className="text-sm text-slate-500">Particuliers</p>
          </div>
          <div className="flex-1 text-center p-4 bg-purple-50 rounded-xl">
            <Building2 className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-600">{stats?.leads?.by_beneficiary_type?.entreprise || 0}</p>
            <p className="text-sm text-slate-500">Entreprises</p>
          </div>
          <div className="flex-1 text-center p-4 bg-blue-50 rounded-xl">
            <Building2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-600">{stats?.leads?.by_beneficiary_type?.collectivite || 0}</p>
            <p className="text-sm text-slate-500">Collectivités</p>
          </div>
          {stats?.leads?.by_beneficiary_type?.legacy > 0 && (
            <div className="flex-1 text-center p-4 bg-slate-50 rounded-xl">
              <Users className="h-8 w-8 text-slate-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-500">{stats?.leads?.by_beneficiary_type?.legacy || 0}</p>
              <p className="text-sm text-slate-500">Non catégorisés</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Leads List
const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [businessFilter, setBusinessFilter] = useState("all");
  const { token } = useAuth();

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (scoreFilter !== "all") params.append("aid_score_label", scoreFilter);
      if (statusFilter !== "all") params.append("lead_status", statusFilter);
      if (typeFilter !== "all") params.append("beneficiary_type", typeFilter);
      if (businessFilter !== "all") params.append("business_priority_label", businessFilter);
      
      const response = await axios.get(`${API}/admin/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data.leads);
    } catch (error) {
      toast.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [scoreFilter, statusFilter, typeFilter, businessFilter]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/leads/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchLeads();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getScoreIcon = (score) => {
    const scoreKey = score || 'tiede';
    switch (scoreKey) {
      case "chaud": return <Flame className="h-4 w-4" />;
      case "tiede": return <Thermometer className="h-4 w-4" />;
      case "froid": return <Snowflake className="h-4 w-4" />;
      default: return <Thermometer className="h-4 w-4" />;
    }
  };

  const getScoreBadgeClass = (score) => {
    const scoreKey = score || 'tiede';
    switch (scoreKey) {
      case "chaud": return "bg-red-100 text-red-700 border-red-200";
      case "tiede": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "froid": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getBusinessBadgeClass = (label) => {
    switch (label) {
      case "haute": return "bg-red-100 text-red-700 border-red-200";
      case "moyenne": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "basse": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "particulier": return { label: "Particulier", class: "bg-green-100 text-green-700" };
      case "entreprise": return { label: "Entreprise", class: "bg-purple-100 text-purple-700" };
      case "collectivite": return { label: "Collectivité", class: "bg-blue-100 text-blue-700" };
      default: return { label: "Particulier", class: "bg-slate-100 text-slate-700" };
    }
  };

  const getLeadName = (lead) => {
    if (lead.beneficiary_type === "entreprise") {
      return lead.company_name || `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim();
    }
    if (lead.beneficiary_type === "collectivite") {
      return lead.collectivity_name || `${lead.contact_first_name || ''} ${lead.contact_last_name || ''}`.trim();
    }
    return `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
  };

  const filteredLeads = leads.filter(lead => {
    const searchLower = searchTerm.toLowerCase();
    const leadName = getLeadName(lead).toLowerCase();
    return (
      leadName.includes(searchLower) ||
      lead.email?.toLowerCase().includes(searchLower) ||
      lead.city?.toLowerCase().includes(searchLower)
    );
  });

  // Support both old and new field names
  const getLeadScore = (lead) => lead.aid_score_label || lead.score_label || lead.score || 'tiede';
  const getLeadStatus = (lead) => lead.lead_status || lead.status || 'nouveau';
  const getBusinessPriority = (lead) => lead.business_priority_label || 'moyenne';

  return (
    <div data-testid="leads-list">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tous les Leads
        </h1>
        <p className="text-slate-500 mt-1">Gérez vos demandes de bénéficiaires (Particuliers, Entreprises, Collectivités)</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-100 p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-type">
            <Users className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="particulier">Particulier</SelectItem>
            <SelectItem value="entreprise">Entreprise</SelectItem>
            <SelectItem value="collectivite">Collectivité</SelectItem>
          </SelectContent>
        </Select>
        <Select value={scoreFilter} onValueChange={setScoreFilter}>
          <SelectTrigger className="w-[150px]" data-testid="filter-score">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Éligibilité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toute éligibilité</SelectItem>
            <SelectItem value="chaud">Chaud</SelectItem>
            <SelectItem value="tiede">Tiède</SelectItem>
            <SelectItem value="froid">Froid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={businessFilter} onValueChange={setBusinessFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-business">
            <TrendingUp className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Priorité" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toute priorité</SelectItem>
            <SelectItem value="haute">Haute</SelectItem>
            <SelectItem value="moyenne">Moyenne</SelectItem>
            <SelectItem value="basse">Basse</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="nouveau">Nouveau</SelectItem>
            <SelectItem value="contacte">Contacté</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="rdv_pris">RDV pris</SelectItem>
            <SelectItem value="converti">Converti</SelectItem>
            <SelectItem value="perdu">Perdu</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Nom</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Type</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Contact</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Éligibilité</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Priorité</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Aide estimée</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Statut</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-slate-500">
                      Aucun lead trouvé
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => {
                    const typeBadge = getTypeBadge(lead.beneficiary_type);
                    return (
                    <tr key={lead.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${lead.id}`}>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{getLeadName(lead)}</p>
                          <p className="text-xs text-slate-400">{lead.city} ({lead.department})</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={`${typeBadge.class} text-xs`}>
                          {typeBadge.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p className="truncate max-w-[150px]">{lead.email}</p>
                          <p className="text-slate-400">{lead.phone}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={`${getScoreBadgeClass(getLeadScore(lead))} flex items-center gap-1 w-fit`}>
                          {getScoreIcon(getLeadScore(lead))}
                          <span className="capitalize">{getLeadScore(lead)}</span>
                          <span className="text-xs opacity-70">({lead.aid_score || lead.score_value || '?'})</span>
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={`${getBusinessBadgeClass(getBusinessPriority(lead))} flex items-center gap-1 w-fit`}>
                          <TrendingUp className="h-3 w-3" />
                          <span className="capitalize">{getBusinessPriority(lead)}</span>
                        </Badge>
                      </td>
                      <td className="p-4">
                        <span className="font-semibold text-[#1A4D2E]">
                          {lead.estimated_aid?.toLocaleString('fr-FR')} €
                        </span>
                      </td>
                      <td className="p-4">
                        <Select 
                          value={getLeadStatus(lead)} 
                          onValueChange={(v) => updateLeadStatus(lead.id, v)}
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`status-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nouveau">Nouveau</SelectItem>
                            <SelectItem value="contacte">Contacté</SelectItem>
                            <SelectItem value="en_cours">En cours</SelectItem>
                            <SelectItem value="rdv_pris">RDV pris</SelectItem>
                            <SelectItem value="converti">Converti</SelectItem>
                            <SelectItem value="perdu">Perdu</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <Link to={`/resultat/${lead.id}`} target="_blank">
                          <Button variant="ghost" size="sm" className="text-[#1A4D2E]" data-testid={`view-lead-${lead.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Partners List
const PartnersList = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data.partners);
    } catch (error) {
      toast.error("Erreur lors du chargement des partenaires");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const updatePartnerStatus = async (partnerId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/partners/${partnerId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchPartners();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div data-testid="partners-list">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Partenaires
        </h1>
        <p className="text-slate-500 mt-1">Gérez vos demandes de partenariat</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Entreprise</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Contact</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Localisation</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Activités</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Certifications</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Statut</th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-slate-500">
                      Aucun partenaire trouvé
                    </td>
                  </tr>
                ) : (
                  partners.map((partner) => (
                    <tr key={partner.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`partner-row-${partner.id}`}>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{partner.company_name}</p>
                          <p className="text-xs text-slate-400">SIRET: {partner.siret}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p>{partner.contact_first_name} {partner.contact_last_name}</p>
                          <p className="text-slate-400">{partner.email}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm">
                          <p>{partner.city}</p>
                          <p className="text-slate-400">{partner.postal_code}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {partner.activity_type?.slice(0, 2).map((act) => (
                            <Badge key={act} variant="outline" className="text-xs capitalize">
                              {act}
                            </Badge>
                          ))}
                          {partner.activity_type?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{partner.activity_type.length - 2}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {partner.certifications?.slice(0, 2).map((cert) => (
                            <Badge key={cert} className="bg-[#E8F5E9] text-[#1A4D2E] text-xs uppercase">
                              {cert}
                            </Badge>
                          ))}
                          {partner.certifications?.length > 2 && (
                            <Badge className="bg-slate-100 text-slate-600 text-xs">
                              +{partner.certifications.length - 2}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Select 
                          value={partner.status} 
                          onValueChange={(v) => updatePartnerStatus(partner.id, v)}
                        >
                          <SelectTrigger className="w-[130px] h-8 text-xs" data-testid={`partner-status-${partner.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="valide">Validé</SelectItem>
                            <SelectItem value="refuse">Refusé</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Appointments Management
const AppointmentsManagement = () => {
  const [slots, setSlots] = useState([]);
  const [callbackRequests, setCallbackRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newSlot, setNewSlot] = useState({ date: "", start_time: "", end_time: "" });
  const { token } = useAuth();

  const fetchData = async () => {
    try {
      const [slotsRes, callbacksRes] = await Promise.all([
        axios.get(`${API}/admin/appointments/slots`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/callback-requests`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSlots(slotsRes.data.slots || []);
      setCallbackRequests(callbacksRes.data.requests || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const createSlot = async () => {
    if (!newSlot.date || !newSlot.start_time || !newSlot.end_time) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    try {
      await axios.post(`${API}/admin/appointments/slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Créneau créé");
      setDialogOpen(false);
      setNewSlot({ date: "", start_time: "", end_time: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const deleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/admin/appointments/slots/${slotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Créneau supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      disponible: "bg-green-100 text-green-700",
      reserve: "bg-yellow-100 text-yellow-700",
      confirme: "bg-blue-100 text-blue-700",
      annule: "bg-red-100 text-red-700",
      termine: "bg-slate-100 text-slate-700"
    };
    return styles[status] || styles.disponible;
  };

  return (
    <div data-testid="appointments-management">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Rendez-vous
          </h1>
          <p className="text-slate-500 mt-1">Gérez les créneaux et demandes de rappel</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1A4D2E] hover:bg-[#143d24]" data-testid="add-slot-btn">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un créneau
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau créneau</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newSlot.date}
                  onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  data-testid="slot-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Heure début</Label>
                  <Input
                    type="time"
                    value={newSlot.start_time}
                    onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                    data-testid="slot-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Heure fin</Label>
                  <Input
                    type="time"
                    value={newSlot.end_time}
                    onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                    data-testid="slot-end-input"
                  />
                </div>
              </div>
              <Button onClick={createSlot} className="w-full bg-[#1A4D2E] hover:bg-[#143d24]" data-testid="create-slot-btn">
                Créer le créneau
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Slots */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4">Créneaux ({slots.length})</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {slots.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucun créneau</p>
              ) : (
                slots.map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">
                        {new Date(slot.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-xs text-slate-500">{slot.start_time} - {slot.end_time}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusBadge(slot.status)}>{slot.status}</Badge>
                      {slot.status === 'disponible' && (
                        <Button variant="ghost" size="sm" onClick={() => deleteSlot(slot.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Callback Requests */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4">Demandes de rappel ({callbackRequests.length})</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {callbackRequests.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucune demande</p>
              ) : (
                callbackRequests.map((req) => (
                  <div key={req.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">{req.lead_name}</p>
                        <p className="text-xs text-slate-500">{req.lead_phone}</p>
                        <p className="text-xs text-slate-500">{req.lead_email}</p>
                      </div>
                      <Badge className={req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                        {req.status}
                      </Badge>
                    </div>
                    {req.preferred_time && (
                      <p className="text-xs text-[#1A4D2E] mt-2">Préférence: {req.preferred_time}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subsidy Sources Management
const SubsidySourcesManagement = () => {
  const [sources, setSources] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkingLoading, setCheckingLoading] = useState(false);
  const [newSource, setNewSource] = useState({ 
    name: "", url: "", source_type: "autre", region: "", department: "", check_frequency_hours: 24 
  });
  const { token } = useAuth();

  const fetchData = async () => {
    try {
      const [sourcesRes, updatesRes] = await Promise.all([
        axios.get(`${API}/admin/subsidy-sources`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/subsidy-updates?limit=20`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSources(sourcesRes.data.sources || []);
      setUpdates(updatesRes.data.updates || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createSource = async () => {
    if (!newSource.name || !newSource.url) {
      toast.error("Nom et URL requis");
      return;
    }
    try {
      await axios.post(`${API}/admin/subsidy-sources`, newSource, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Source ajoutée");
      setDialogOpen(false);
      setNewSource({ name: "", url: "", source_type: "autre", region: "", department: "", check_frequency_hours: 24 });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const deleteSource = async (sourceId) => {
    try {
      await axios.delete(`${API}/admin/subsidy-sources/${sourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Source supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleSource = async (sourceId) => {
    try {
      await axios.put(`${API}/admin/subsidy-sources/${sourceId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const triggerCheck = async () => {
    setCheckingLoading(true);
    try {
      await axios.post(`${API}/admin/trigger-subsidy-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vérification lancée en arrière-plan");
      
      // Attendre quelques secondes puis rafraîchir les données
      setTimeout(async () => {
        await fetchData();
        setCheckingLoading(false);
        toast.info("Données mises à jour");
      }, 8000);
    } catch (error) {
      toast.error("Erreur lors du lancement");
      setCheckingLoading(false);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      anah: "bg-green-100 text-green-700",
      cee: "bg-blue-100 text-blue-700",
      region: "bg-purple-100 text-purple-700",
      departement: "bg-orange-100 text-orange-700",
      commune: "bg-pink-100 text-pink-700",
      autre: "bg-slate-100 text-slate-700"
    };
    return colors[type] || colors.autre;
  };

  return (
    <div data-testid="subsidy-sources">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Veille Aides
          </h1>
          <p className="text-slate-500 mt-1">Surveillez les sources d'aides à la rénovation</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={triggerCheck}
            disabled={checkingLoading}
            className="border-[#1A4D2E] text-[#1A4D2E]"
            data-testid="trigger-check-btn"
          >
            {checkingLoading ? (
              <span className="flex items-center">
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-[#1A4D2E] border-t-transparent mr-2"></span>
                Vérification en cours...
              </span>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Vérifier maintenant
              </>
            )}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]" data-testid="add-source-btn">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouvelle source</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nom de la source</Label>
                  <Input
                    value={newSource.name}
                    onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                    placeholder="ANAH - MaPrimeRénov'"
                    data-testid="source-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL</Label>
                  <Input
                    value={newSource.url}
                    onChange={(e) => setNewSource({ ...newSource, url: e.target.value })}
                    placeholder="https://www.anah.fr/..."
                    data-testid="source-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type de source</Label>
                  <Select value={newSource.source_type} onValueChange={(v) => setNewSource({ ...newSource, source_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="anah">ANAH</SelectItem>
                      <SelectItem value="cee">CEE</SelectItem>
                      <SelectItem value="region">Région</SelectItem>
                      <SelectItem value="departement">Département</SelectItem>
                      <SelectItem value="commune">Commune</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Région (optionnel)</Label>
                    <Input
                      value={newSource.region}
                      onChange={(e) => setNewSource({ ...newSource, region: e.target.value })}
                      placeholder="ile-de-france"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Département (optionnel)</Label>
                    <Input
                      value={newSource.department}
                      onChange={(e) => setNewSource({ ...newSource, department: e.target.value })}
                      placeholder="75"
                    />
                  </div>
                </div>
                <Button onClick={createSource} className="w-full bg-[#1A4D2E] hover:bg-[#143d24]" data-testid="create-source-btn">
                  Ajouter la source
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Sources */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4">Sources surveillées ({sources.length})</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {sources.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucune source</p>
              ) : (
                sources.map((source) => (
                  <div key={source.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">{source.name}</p>
                          <Badge className={getTypeColor(source.source_type)}>{source.source_type}</Badge>
                        </div>
                        <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                          {source.url.substring(0, 40)}...
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        {source.last_checked && (
                          <p className="text-xs text-slate-400 mt-1">
                            Dernière vérif: {new Date(source.last_checked).toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={source.is_active}
                          onCheckedChange={() => toggleSource(source.id)}
                        />
                        <Button variant="ghost" size="sm" onClick={() => deleteSource(source.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Updates */}
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4">Changements détectés ({updates.length})</h3>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {updates.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucun changement détecté</p>
              ) : (
                updates.map((update) => (
                  <div key={update.id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <Badge className="bg-[#E8F5E9] text-[#1A4D2E]">{update.change_type}</Badge>
                      <span className="text-xs text-slate-400">
                        {new Date(update.detected_at).toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <p className="text-sm">{update.change_summary}</p>
                    {update.ai_analysis && (
                      <p className="text-xs text-slate-500 mt-2 italic">{update.ai_analysis}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Notifications Management
const NotificationsManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const { token } = useAuth();

  const fetchNotifications = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/notifications${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      sent: "bg-green-100 text-green-700",
      failed: "bg-red-100 text-red-700"
    };
    return styles[status] || styles.pending;
  };

  return (
    <div data-testid="notifications-management">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notifications
          </h1>
          <p className="text-slate-500 mt-1">Historique des notifications envoyées</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="sent">Envoyées</SelectItem>
            <SelectItem value="failed">Échouées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Email</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Sujet</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Type</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Statut</th>
                  <th className="text-left p-4 font-semibold text-sm text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-slate-500">
                      Aucune notification
                    </td>
                  </tr>
                ) : (
                  notifications.map((notif) => (
                    <tr key={notif.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-sm">{notif.user_email}</td>
                      <td className="p-4 text-sm font-medium">{notif.subject}</td>
                      <td className="p-4">
                        <Badge variant="outline">{notif.notification_type}</Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={getStatusBadge(notif.status)}>{notif.status}</Badge>
                      </td>
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(notif.created_at).toLocaleString('fr-FR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Score Adjustments Management
const ScoreAdjustmentsManagement = () => {
  const [proposals, setProposals] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, applied: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const { token } = useAuth();

  const fetchProposals = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/admin/score-adjustments${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProposals(response.data.proposals || []);
      setCounts(response.data.counts || {});
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, [statusFilter]);

  const handleReview = async (action) => {
    if (!selectedProposal) return;
    setReviewLoading(true);
    try {
      await axios.post(
        `${API}/admin/score-adjustments/${selectedProposal.id}/review`,
        { action, notes: reviewNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'approve' ? "Ajustements approuvés et appliqués" : "Proposition rejetée");
      setReviewDialogOpen(false);
      setSelectedProposal(null);
      setReviewNotes("");
      fetchProposals();
    } catch (error) {
      toast.error("Erreur lors du traitement");
    } finally {
      setReviewLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { class: "bg-yellow-100 text-yellow-700", icon: AlertTriangle },
      approved: { class: "bg-blue-100 text-blue-700", icon: CheckCircle },
      applied: { class: "bg-green-100 text-green-700", icon: CheckCircle },
      rejected: { class: "bg-red-100 text-red-700", icon: XCircle }
    };
    return styles[status] || styles.pending;
  };

  const getChangeTypeBadge = (type) => {
    const styles = {
      nouvelle_aide: "bg-green-100 text-green-700",
      modification: "bg-blue-100 text-blue-700",
      fin: "bg-red-100 text-red-700",
      budget_epuise: "bg-orange-100 text-orange-700",
      criteres_modifies: "bg-purple-100 text-purple-700"
    };
    return styles[type] || "bg-slate-100 text-slate-700";
  };

  return (
    <div data-testid="score-adjustments">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scoring Dynamique
          </h1>
          <p className="text-slate-500 mt-1">Gérez les ajustements de score recommandés par l'IA</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes ({proposals.length})</SelectItem>
            <SelectItem value="pending">En attente ({counts.pending})</SelectItem>
            <SelectItem value="approved">Approuvées ({counts.approved})</SelectItem>
            <SelectItem value="applied">Appliquées ({counts.applied})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-700">En attente</span>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{counts.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Approuvées</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">{counts.approved}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">Appliquées</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{counts.applied}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {proposals.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-100 p-8 text-center">
              <Zap className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune proposition d'ajustement</p>
              <p className="text-sm text-slate-400 mt-1">Les propositions apparaîtront ici quand la veille détectera des changements</p>
            </div>
          ) : (
            proposals.map((proposal) => {
              const statusConfig = getStatusBadge(proposal.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div key={proposal.id} className="bg-white rounded-xl border border-slate-100 p-6" data-testid={`proposal-${proposal.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getChangeTypeBadge(proposal.change_type)}>
                          {proposal.change_type?.replace(/_/g, ' ')}
                        </Badge>
                        <Badge className={statusConfig.class}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {proposal.status}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-[#1D1D1D]">{proposal.change_summary}</h3>
                      <p className="text-sm text-slate-500 mt-1">{proposal.ai_recommendation}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#1A4D2E]">{proposal.total_impacted}</p>
                      <p className="text-xs text-slate-500">leads impactés</p>
                    </div>
                  </div>

                  {/* Sample of impacted leads */}
                  {proposal.impacted_leads?.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium text-slate-700 mb-3">Aperçu des ajustements recommandés:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {proposal.impacted_leads.slice(0, 5).map((lead, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-white rounded p-2">
                            <span className="font-medium">{lead.lead_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">{lead.current_score_value}</span>
                              <span className="text-slate-400">→</span>
                              <span className={lead.recommended_adjustment > 0 ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                                {lead.new_score_value} ({lead.recommended_adjustment > 0 ? '+' : ''}{lead.recommended_adjustment})
                              </span>
                            </div>
                          </div>
                        ))}
                        {proposal.impacted_leads.length > 5 && (
                          <p className="text-xs text-slate-400 text-center pt-2">
                            + {proposal.impacted_leads.length - 5} autres leads
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-400">
                      Créé le {new Date(proposal.created_at).toLocaleString('fr-FR')}
                    </p>
                    {proposal.status === 'pending' && (
                      <div className="flex gap-2">
                        <Dialog open={reviewDialogOpen && selectedProposal?.id === proposal.id} onOpenChange={(open) => {
                          setReviewDialogOpen(open);
                          if (!open) setSelectedProposal(null);
                        }}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => setSelectedProposal(proposal)}
                              data-testid={`reject-${proposal.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Rejeter
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Confirmer le rejet</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <p className="text-sm text-slate-600">
                                Voulez-vous rejeter cette proposition d'ajustement pour {selectedProposal?.total_impacted} leads ?
                              </p>
                              <div className="space-y-2">
                                <Label>Notes (optionnel)</Label>
                                <Textarea
                                  value={reviewNotes}
                                  onChange={(e) => setReviewNotes(e.target.value)}
                                  placeholder="Raison du rejet..."
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setReviewDialogOpen(false)} className="flex-1">
                                  Annuler
                                </Button>
                                <Button 
                                  onClick={() => handleReview('reject')} 
                                  disabled={reviewLoading}
                                  className="flex-1 bg-red-600 hover:bg-red-700"
                                >
                                  {reviewLoading ? "..." : "Confirmer le rejet"}
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        
                        <Button 
                          size="sm" 
                          className="bg-[#1A4D2E] hover:bg-[#143d24]"
                          onClick={() => {
                            setSelectedProposal(proposal);
                            handleReview('approve');
                          }}
                          disabled={reviewLoading}
                          data-testid={`approve-${proposal.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approuver et appliquer
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Geographic Statistics Dashboard
const GeographicStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const { token } = useAuth();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("beneficiary_type", typeFilter);
      params.append("period", periodFilter);
      
      const response = await axios.get(`${API}/admin/stats/geographic?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [typeFilter, periodFilter]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("beneficiary_type", typeFilter);
      params.append("period", periodFilter);
      
      const response = await axios.get(`${API}/admin/stats/geographic/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stats_geo_${periodFilter}${typeFilter !== 'all' ? '_' + typeFilter : ''}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export CSV téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const getRegionDisplayName = (region) => {
    if (!region) return "Non définie";
    return region.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-');
  };

  const getHotRateColor = (rate) => {
    if (rate >= 70) return "text-red-600 bg-red-50";
    if (rate >= 40) return "text-yellow-600 bg-yellow-50";
    return "text-blue-600 bg-blue-50";
  };

  const getBusinessScoreColor = (score) => {
    if (score >= 70) return "text-red-600";
    if (score >= 40) return "text-yellow-600";
    return "text-blue-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="geographic-stats">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Statistiques Géographiques
          </h1>
          <p className="text-slate-500 mt-1">Analyse territoriale pour décision business</p>
        </div>
        <Button 
          onClick={handleExportCSV}
          disabled={exporting}
          className="bg-[#1A4D2E] hover:bg-[#143d24]"
          data-testid="export-csv-btn"
        >
          {exporting ? (
            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-100 p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-slate-400" />
          <span className="text-sm text-slate-600 font-medium">Filtres :</span>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-type-geo">
            <Users className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="particulier">Particuliers</SelectItem>
            <SelectItem value="entreprise">Entreprises</SelectItem>
            <SelectItem value="collectivite">Collectivités</SelectItem>
          </SelectContent>
        </Select>
        <Select value={periodFilter} onValueChange={setPeriodFilter}>
          <SelectTrigger className="w-[150px]" data-testid="filter-period-geo">
            <Clock className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toute période</SelectItem>
            <SelectItem value="7d">7 derniers jours</SelectItem>
            <SelectItem value="30d">30 derniers jours</SelectItem>
            <SelectItem value="90d">90 derniers jours</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 ml-auto">
          {stats?.total_leads || 0} leads analysés
        </span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-5 w-5 text-[#1A4D2E]" />
            <span className="text-sm text-slate-500">Régions</span>
          </div>
          <p className="text-2xl font-bold text-[#1D1D1D]">{stats?.by_region?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-slate-500">Départements</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{stats?.by_department?.length || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="h-5 w-5 text-red-500" />
            <span className="text-sm text-slate-500">Leads chauds total</span>
          </div>
          <p className="text-2xl font-bold text-red-500">
            {stats?.by_region?.reduce((sum, r) => sum + (r.hot_leads || 0), 0) || 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Euro className="h-5 w-5 text-[#1A4D2E]" />
            <span className="text-sm text-slate-500">Aides potentielles</span>
          </div>
          <p className="text-2xl font-bold text-[#1A4D2E]">
            {(stats?.by_region?.reduce((sum, r) => sum + (r.total_estimated_aid || 0), 0) || 0).toLocaleString('fr-FR')} €
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Top 10 Zones by Business Priority */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#E07A5F]" />
            Top 10 Zones par Priorité Business
          </h3>
          {stats?.top_10_zones_by_business_priority?.length > 0 ? (
            <div className="space-y-3">
              {stats.top_10_zones_by_business_priority.map((zone, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx < 3 ? 'bg-[#E07A5F] text-white' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="font-medium text-[#1D1D1D]">Dept. {zone.department}</p>
                      <p className="text-xs text-slate-500">{getRegionDisplayName(zone.region)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${getBusinessScoreColor(zone.avg_business_score)}`}>
                      {zone.avg_business_score}/100
                    </p>
                    <p className="text-xs text-slate-500">{zone.total_leads} leads</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Aucune donnée disponible</p>
          )}
        </div>

        {/* Hot Rate by Region */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4 flex items-center gap-2">
            <Flame className="h-5 w-5 text-red-500" />
            Taux de Leads Chauds par Région
          </h3>
          {stats?.by_region?.length > 0 ? (
            <div className="space-y-3">
              {stats.by_region.slice(0, 8).map((region, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#1D1D1D]">
                        {getRegionDisplayName(region.region)}
                      </span>
                      <span className={`text-sm font-bold px-2 py-0.5 rounded ${getHotRateColor(region.hot_rate)}`}>
                        {region.hot_rate}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          region.hot_rate >= 70 ? 'bg-red-500' :
                          region.hot_rate >= 40 ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${region.hot_rate}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>{region.hot_leads} chauds / {region.total_leads} total</span>
                      <span>{region.total_estimated_aid?.toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">Aucune donnée disponible</p>
          )}
        </div>
      </div>

      {/* Department Table */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-semibold text-lg text-[#1D1D1D] flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#1A4D2E]" />
            Détail par Département
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 font-semibold text-sm text-slate-600">Département</th>
                <th className="text-left p-4 font-semibold text-sm text-slate-600">Région</th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600">Leads</th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600">Score Éligibilité Moy.</th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600">Score Business Moy.</th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600">Taux Chauds</th>
                <th className="text-center p-4 font-semibold text-sm text-slate-600">Priorité Haute</th>
                <th className="text-right p-4 font-semibold text-sm text-slate-600">Aides Estimées</th>
              </tr>
            </thead>
            <tbody>
              {stats?.by_department?.length > 0 ? (
                stats.by_department.map((dept, idx) => (
                  <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-4 font-medium">{dept.department}</td>
                    <td className="p-4 text-slate-500 text-sm">{getRegionDisplayName(dept.region)}</td>
                    <td className="p-4 text-center">{dept.total_leads}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        dept.avg_aid_score >= 70 ? 'bg-red-100 text-red-700' :
                        dept.avg_aid_score >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {dept.avg_aid_score}/100
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-medium ${
                        dept.avg_business_score >= 70 ? 'bg-red-100 text-red-700' :
                        dept.avg_business_score >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {dept.avg_business_score}/100
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm font-bold ${getHotRateColor(dept.hot_rate)}`}>
                        {dept.hot_rate}%
                      </span>
                    </td>
                    <td className="p-4 text-center font-medium text-red-600">{dept.high_priority}</td>
                    <td className="p-4 text-right font-semibold text-[#1A4D2E]">
                      {dept.total_estimated_aid?.toLocaleString('fr-FR')} €
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-slate-500">
                    Aucun département trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Settings Management (Anti-spam, Cron & Email Settings)
const SettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [manualCheckLoading, setManualCheckLoading] = useState(false);
  const { token } = useAuth();

  const fetchCronStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/cron-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronStatus(res.data);
    } catch (error) {
      console.error("Erreur fetch cron status:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const [settingsRes, cronRes, emailRes] = await Promise.all([
        axios.get(`${API}/admin/notification-settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cron/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/email/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSettings(settingsRes.data);
      setCronStatus(cronRes.data);
      setEmailStatus(emailRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/notification-settings`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: settings
      });
      setSettings(response.data);
      toast.success("Paramètres enregistrés");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Veuillez saisir une adresse email");
      return;
    }
    setTestingEmail(true);
    try {
      await axios.post(`${API}/admin/email/test`, 
        { to_email: testEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email de test envoyé à ${testEmail}`);
      fetchSettings(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1A4D2E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-management">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Paramètres
        </h1>
        <p className="text-slate-500 mt-1">Configuration anti-spam et notifications</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Anti-spam Settings */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-6 flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#1A4D2E]" />
            Règles Anti-spam
          </h3>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Notifications max par jour</Label>
              <Input
                type="number"
                value={settings?.max_notifications_per_day || 3}
                onChange={(e) => setSettings({ ...settings, max_notifications_per_day: parseInt(e.target.value) })}
                className="w-32"
                data-testid="max-per-day"
              />
              <p className="text-xs text-slate-400">Limite quotidienne par utilisateur</p>
            </div>

            <div className="space-y-2">
              <Label>Notifications max par semaine</Label>
              <Input
                type="number"
                value={settings?.max_notifications_per_week || 10}
                onChange={(e) => setSettings({ ...settings, max_notifications_per_week: parseInt(e.target.value) })}
                className="w-32"
                data-testid="max-per-week"
              />
              <p className="text-xs text-slate-400">Limite hebdomadaire par utilisateur</p>
            </div>

            <div className="space-y-2">
              <Label>Heures minimum entre notifications</Label>
              <Input
                type="number"
                value={settings?.min_hours_between_notifications || 24}
                onChange={(e) => setSettings({ ...settings, min_hours_between_notifications: parseInt(e.target.value) })}
                className="w-32"
                data-testid="min-hours"
              />
              <p className="text-xs text-slate-400">Délai minimum entre deux envois</p>
            </div>
          </div>
        </div>

        {/* Scoring Settings */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-6 flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#E07A5F]" />
            Scoring Dynamique
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label>Validation admin requise</Label>
                <p className="text-xs text-slate-400 mt-1">Les ajustements doivent être approuvés manuellement</p>
              </div>
              <Switch
                checked={settings?.admin_validation_required ?? true}
                onCheckedChange={(checked) => setSettings({ ...settings, admin_validation_required: checked })}
                data-testid="admin-validation-toggle"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Changement significatif requis</Label>
                <p className="text-xs text-slate-400 mt-1">Notifier seulement si l'ajustement est important</p>
              </div>
              <Switch
                checked={settings?.require_significant_change ?? true}
                onCheckedChange={(checked) => setSettings({ ...settings, require_significant_change: checked })}
                data-testid="significant-change-toggle"
              />
            </div>

            {settings?.require_significant_change && (
              <div className="space-y-2">
                <Label>Seuil de changement significatif (points)</Label>
                <Input
                  type="number"
                  value={settings?.significant_change_threshold || 10}
                  onChange={(e) => setSettings({ ...settings, significant_change_threshold: parseInt(e.target.value) })}
                  className="w-32"
                  data-testid="threshold"
                />
                <p className="text-xs text-slate-400">Ajustement minimum pour déclencher une notification</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email & Cron Configuration */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        {/* Email/Resend Configuration */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-600" />
            Configuration Email (Resend)
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${emailStatus?.resend_configured ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className={emailStatus?.resend_configured ? 'text-green-700' : 'text-red-700'}>
                {emailStatus?.resend_configured ? 'Resend configuré' : 'Resend non configuré'}
              </span>
            </div>
            
            {emailStatus?.resend_api_key_preview && (
              <p className="text-sm text-slate-500">
                Clé API: <code className="bg-slate-100 px-2 py-1 rounded">{emailStatus.resend_api_key_preview}</code>
              </p>
            )}
            
            <p className="text-sm text-slate-500">
              Expéditeur: <code className="bg-slate-100 px-2 py-1 rounded">{emailStatus?.sender_email}</code>
            </p>
            
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{emailStatus?.stats?.sent || 0}</p>
                <p className="text-xs text-slate-500">Envoyés</p>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{emailStatus?.stats?.pending || 0}</p>
                <p className="text-xs text-slate-500">En attente</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{emailStatus?.stats?.failed || 0}</p>
                <p className="text-xs text-slate-500">Échecs</p>
              </div>
            </div>

            {emailStatus?.resend_configured && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Label className="mb-2 block">Envoyer un email de test</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1"
                    data-testid="test-email-input"
                  />
                  <Button
                    onClick={sendTestEmail}
                    disabled={testingEmail}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="send-test-email-btn"
                  >
                    {testingEmail ? "Envoi..." : "Tester"}
                  </Button>
                </div>
              </div>
            )}

            {!emailStatus?.resend_configured && emailStatus?.instructions_if_not_configured && (
              <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="font-medium text-yellow-800 mb-2">Configuration requise :</p>
                <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
                  <li>{emailStatus.instructions_if_not_configured.step1}</li>
                  <li>{emailStatus.instructions_if_not_configured.step2}</li>
                  <li>{emailStatus.instructions_if_not_configured.step3}</li>
                </ol>
              </div>
            )}
          </div>
        </div>

        {/* Cron Configuration */}
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-lg text-[#1D1D1D] mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Veille Automatique (Cron)
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${cronStatus?.cron_secret_configured ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className={cronStatus?.cron_secret_configured ? 'text-green-700' : 'text-yellow-700'}>
                {cronStatus?.cron_secret_configured ? 'Cron sécurisé configuré' : 'Cron non configuré'}
              </span>
            </div>

            {/* Last Execution Metrics */}
            {cronStatus?.last_execution && (
              <div className={`p-4 rounded-lg border ${
                cronStatus.last_execution.status === 'success' ? 'bg-green-50 border-green-200' :
                cronStatus.last_execution.status === 'failed' ? 'bg-red-50 border-red-200' :
                cronStatus.last_execution.status === 'running' ? 'bg-blue-50 border-blue-200' :
                'bg-yellow-50 border-yellow-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-slate-700">Dernière exécution</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    cronStatus.last_execution.status === 'success' ? 'bg-green-100 text-green-700' :
                    cronStatus.last_execution.status === 'failed' ? 'bg-red-100 text-red-700' :
                    cronStatus.last_execution.status === 'running' ? 'bg-blue-100 text-blue-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {cronStatus.last_execution.status === 'success' ? '✓ Succès' :
                     cronStatus.last_execution.status === 'failed' ? '✗ Échec' :
                     cronStatus.last_execution.status === 'running' ? '⟳ En cours' :
                     '⏱ Timeout'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Date :</span>
                    <span className="ml-2 font-medium">
                      {cronStatus.last_execution.started_at ? 
                        new Date(cronStatus.last_execution.started_at).toLocaleString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Durée :</span>
                    <span className="ml-2 font-medium">
                      {cronStatus.last_execution.duration_seconds ? 
                        `${cronStatus.last_execution.duration_seconds}s` : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mt-3">
                  <div className="text-center p-2 bg-white/60 rounded">
                    <p className="text-lg font-bold text-slate-700">{cronStatus.last_execution.sources_checked || 0}</p>
                    <p className="text-xs text-slate-500">Sources</p>
                  </div>
                  <div className="text-center p-2 bg-white/60 rounded">
                    <p className="text-lg font-bold text-orange-600">{cronStatus.last_execution.updates_created || 0}</p>
                    <p className="text-xs text-slate-500">Updates</p>
                  </div>
                  <div className="text-center p-2 bg-white/60 rounded">
                    <p className="text-lg font-bold text-blue-600">{cronStatus.last_execution.notifications_queued || 0}</p>
                    <p className="text-xs text-slate-500">En file</p>
                  </div>
                  <div className="text-center p-2 bg-white/60 rounded">
                    <p className="text-lg font-bold text-green-600">{cronStatus.last_execution.notifications_sent || 0}</p>
                    <p className="text-xs text-slate-500">Envoyés</p>
                  </div>
                </div>

                {cronStatus.last_execution.error_message && (
                  <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700">
                    <strong>Erreur :</strong> {cronStatus.last_execution.error_message}
                  </div>
                )}
              </div>
            )}

            {!cronStatus?.last_execution && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <p className="text-slate-500 text-sm">Aucune exécution enregistrée</p>
                <p className="text-xs text-slate-400 mt-1">Configurez votre cron externe pour lancer la première veille</p>
              </div>
            )}

            {/* Execution Stats */}
            {cronStatus?.execution_stats && (
              <div className="flex gap-3">
                <div className="flex-1 text-center p-2 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{cronStatus.execution_stats.total_success || 0}</p>
                  <p className="text-xs text-slate-500">Succès</p>
                </div>
                <div className="flex-1 text-center p-2 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-600">{cronStatus.execution_stats.total_failed || 0}</p>
                  <p className="text-xs text-slate-500">Échecs</p>
                </div>
                <div className="flex-1 text-center p-2 bg-yellow-50 rounded-lg">
                  <p className="text-xl font-bold text-yellow-600">{cronStatus.execution_stats.total_timeout || 0}</p>
                  <p className="text-xs text-slate-500">Timeouts</p>
                </div>
              </div>
            )}

            {/* Bouton Démarrer Manuellement */}
            <div className="p-4 bg-[#E8F5E9] rounded-lg border border-[#1A4D2E]/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-[#1A4D2E]">Lancer une vérification manuelle</p>
                  <p className="text-xs text-slate-600 mt-1">Vérifie toutes les sources actives immédiatement</p>
                </div>
                <Button 
                  onClick={async () => {
                    setManualCheckLoading(true);
                    try {
                      await axios.post(`${API}/admin/trigger-subsidy-check`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success("Vérification lancée ! Patientez quelques secondes...");
                      setTimeout(() => {
                        fetchCronStatus();
                        setManualCheckLoading(false);
                        toast.info("Vérification terminée");
                      }, 10000);
                    } catch (error) {
                      toast.error("Erreur lors du lancement");
                      setManualCheckLoading(false);
                    }
                  }}
                  disabled={manualCheckLoading}
                  className="bg-[#1A4D2E] hover:bg-[#143d24] text-white"
                >
                  {manualCheckLoading ? (
                    <span className="flex items-center">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                      Vérification...
                    </span>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Démarrer maintenant
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-2">Endpoint de déclenchement :</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white px-2 py-1 rounded block overflow-x-auto flex-1">
                  POST {cronStatus?.cron_endpoint}?token={cronStatus?.cron_secret_preview || "***"}
                </code>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/cron/trigger-monitoring?token=${cronStatus?.cron_secret_full || ''}`);
                    toast.success("URL copiée !");
                  }}
                >
                  Copier l'URL complète
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Planning recommandé : <code className="bg-white px-1 rounded">{cronStatus?.recommended_schedule}</code> (6h Paris)
              </p>
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                Sources actives ({cronStatus?.active_sources_count || 0})
              </summary>
              {cronStatus?.last_source_checks?.length > 0 && (
                <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                  {cronStatus.last_source_checks.map((check, idx) => (
                    <div key={idx} className="flex justify-between text-xs p-2 bg-slate-50 rounded">
                      <span className="truncate flex-1">{check.source}</span>
                      <span className="text-slate-400 ml-2">
                        {check.last_checked ? new Date(check.last_checked).toLocaleDateString('fr-FR') : 'Jamais'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </details>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-[#1A4D2E] hover:bg-[#143d24]"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
              Enregistrement...
            </span>
          ) : (
            "Enregistrer les paramètres"
          )}
        </Button>
      </div>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  return (
    <div className="flex min-h-screen bg-[#FAF9F6]">
      <Sidebar />
      <main className="flex-1 p-8 overflow-auto">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="leads" element={<LeadsList />} />
          <Route path="geographic" element={<GeographicStats />} />
          <Route path="partners" element={<PartnersList />} />
          <Route path="appointments" element={<AppointmentsManagement />} />
          <Route path="programs" element={<AdminProgramsPage />} />
          <Route path="subsidy-sources" element={<SubsidySourcesManagement />} />
          <Route path="score-adjustments" element={<ScoreAdjustmentsManagement />} />
          <Route path="notifications" element={<NotificationsManagement />} />
          <Route path="settings" element={<SettingsManagement />} />
        </Routes>
      </main>
    </div>
  );
}
